<template>
    <div class="grid gap-4 xl:grid-cols-2">
        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="visitor-switch-title">
            <h2 id="visitor-switch-title" class="text-base font-semibold text-gray-90">
                游客身份
            </h2>
            <p class="mt-2 text-sm text-gray-60">
                生成新的 visitor_id，并清理当前游客的业务数据。
            </p>

            <div class="mt-5 rounded-md border border-gray-30 bg-gray-10 p-4">
                <div class="text-sm text-gray-60">
                    当前 visitor_id
                </div>
                <code class="mt-2 block break-all text-sm text-gray-90">
                    {{ visitorId || '未设置' }}
                </code>
            </div>

            <div class="mt-5 flex flex-wrap gap-3">
                <button
                    type="button"
                    class="th-btn th-btn-secondary"
                    @click="switchVisitor"
                >
                    切换游客
                </button>
            </div>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="novelty-version-debug-title">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <h2 id="novelty-version-debug-title" class="text-base font-semibold text-gray-90">
                        强制开启新版查新
                    </h2>
                    <p class="mt-2 text-sm text-gray-60">
                        控制新版查新版本切换的调试开关，状态会保存到当前浏览器的 localStorage。
                    </p>
                </div>

                <button
                    type="button"
                    role="switch"
                    class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-eureka-500 focus:ring-offset-2"
                    :class="forceEnableNewVersion ? 'bg-eureka-500' : 'bg-gray-300'"
                    :aria-checked="forceEnableNewVersion"
                    aria-label="是否强制开启新版查新"
                    @click="toggleForceEnableNewVersion"
                >
                    <span
                        class="pointer-events-none inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white shadow transition-transform"
                        :class="forceEnableNewVersion ? 'translate-x-5' : 'translate-x-0.5'"
                    />
                </button>
            </div>

            <div class="mt-5 text-sm text-gray-60">
                当前状态：<span class="font-medium text-gray-90">{{ forceEnableNewVersion ? '开启' : '关闭' }}</span>
            </div>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="invalid-search-debug-title">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <h2 id="invalid-search-debug-title" class="text-base font-semibold text-gray-90">
                        强制开启专利无效分析
                    </h2>
                    <p class="mt-2 text-sm text-gray-60">
                        开启后，当前浏览器可展示并访问专利无效分析，状态会保存到 localStorage。
                    </p>
                </div>

                <button
                    type="button"
                    role="switch"
                    class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-eureka-500 focus:ring-offset-2"
                    :class="forceEnableInvalidSearch ? 'bg-eureka-500' : 'bg-gray-300'"
                    :aria-checked="forceEnableInvalidSearch"
                    aria-label="是否强制开启专利无效分析"
                    @click="toggleForceEnableInvalidSearch"
                >
                    <span
                        class="pointer-events-none inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white shadow transition-transform"
                        :class="forceEnableInvalidSearch ? 'translate-x-5' : 'translate-x-0.5'"
                    />
                </button>
            </div>

            <div class="mt-5 text-sm text-gray-60">
                当前状态：<span class="font-medium text-gray-90">{{ forceEnableInvalidSearch ? '开启' : '关闭' }}</span>
            </div>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="track-event-debug-title">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <h2 id="track-event-debug-title" class="text-base font-semibold text-gray-90">
                        埋点信息调试
                    </h2>
                    <p class="mt-2 text-sm text-gray-60">
                        开启后在页面右下角展示最近发出的埋点事件，状态会保存到当前浏览器的 localStorage。
                    </p>
                </div>

                <button
                    type="button"
                    role="switch"
                    class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-eureka-500 focus:ring-offset-2"
                    :class="trackEventDebugEnabled ? 'bg-eureka-500' : 'bg-gray-300'"
                    :aria-checked="trackEventDebugEnabled"
                    aria-label="是否开启埋点信息调试"
                    @click="toggleTrackEventDebug"
                >
                    <span
                        class="pointer-events-none inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white shadow transition-transform"
                        :class="trackEventDebugEnabled ? 'translate-x-5' : 'translate-x-0.5'"
                    />
                </button>
            </div>

            <div class="mt-5 text-sm text-gray-60">
                当前状态：<span class="font-medium text-gray-90">{{ trackEventDebugEnabled ? '开启' : '关闭' }}</span>
            </div>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="debug-region-title">
            <div>
                <h2 id="debug-region-title" class="text-base font-semibold text-gray-90">
                    切换版本区域
                </h2>
                <p class="mt-2 text-sm text-gray-60">
                    未设置时使用当前部署环境；选择中国版或美国版后，页面会自动刷新使配置生效。
                </p>
            </div>

            <div
                class="mt-5 flex flex-wrap gap-2"
                role="group"
                aria-label="版本区域调试设置"
            >
                <button
                    type="button"
                    class="rounded border px-4 py-2 text-sm font-medium transition-colors"
                    :class="debugRegionOverride === null ? 'border-eureka-500 bg-eureka-500 text-white' : 'border-gray-30 bg-white text-gray-70 hover:bg-gray-10'"
                    :aria-pressed="debugRegionOverride === null"
                    @click="handleDebugRegionChange(null)"
                >
                    未设置
                </button>
                <button
                    type="button"
                    class="rounded border px-4 py-2 text-sm font-medium transition-colors"
                    :class="debugRegionOverride === 'cn' ? 'border-eureka-500 bg-eureka-500 text-white' : 'border-gray-30 bg-white text-gray-70 hover:bg-gray-10'"
                    :aria-pressed="debugRegionOverride === 'cn'"
                    @click="handleDebugRegionChange('cn')"
                >
                    中国版
                </button>
                <button
                    type="button"
                    class="rounded border px-4 py-2 text-sm font-medium transition-colors"
                    :class="debugRegionOverride === 'us' ? 'border-eureka-500 bg-eureka-500 text-white' : 'border-gray-30 bg-white text-gray-70 hover:bg-gray-10'"
                    :aria-pressed="debugRegionOverride === 'us'"
                    @click="handleDebugRegionChange('us')"
                >
                    美国版
                </button>
            </div>

            <div class="mt-5 text-sm text-gray-60">
                当前设置：<span class="font-medium text-gray-90">{{ debugRegionOverride === 'cn' ? '中国版' : debugRegionOverride === 'us' ? '美国版' : '未设置（使用当前环境）' }}</span>
            </div>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="onboarding-reset-title">
            <h2 id="onboarding-reset-title" class="text-base font-semibold text-gray-90">
                Onboarding 状态
            </h2>
            <p class="mt-2 text-sm text-gray-60">
                将当前账号重置为可重新展示 Welcome、Agent、输入框及三个报告页引导的状态。
            </p>

            <div class="mt-5 rounded-md border border-gray-30 bg-gray-10 p-4">
                <div class="text-sm text-gray-60">
                    当前账号 ID
                </div>
                <code class="mt-2 block break-all text-sm text-gray-90">
                    {{ currentAccountId || '未登录' }}
                </code>
            </div>

            <button
                type="button"
                class="th-btn th-btn-primary mt-5"
                :disabled="!canResetOnboarding || onboardingResetStatus === 'resetting'"
                :aria-busy="onboardingResetStatus === 'resetting'"
                @click="resetOnboardingState"
            >
                {{ onboardingResetStatus === 'resetting' ? '重置中…' : '重置并刷新' }}
            </button>

            <p
                v-if="onboardingResetStatus === 'failed'"
                class="mt-3 text-sm text-red-500"
                role="alert"
            >
                重置失败，请检查用户设置接口后重试。
            </p>
        </section>

        <section class="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="first-task-reward-reset-title">
            <h2 id="first-task-reward-reset-title" class="text-base font-semibold text-gray-90">
                首次任务积分奖励
            </h2>
            <p class="mt-2 text-sm text-gray-60">
                清空当前账号的后端赠送记录，并恢复权益卡片和 Home 奖励条的展示状态。
            </p>

            <div class="mt-5 rounded-md border border-gray-30 bg-gray-10 p-4">
                <div class="text-sm text-gray-60">
                    当前账号 ID
                </div>
                <code class="mt-2 block break-all text-sm text-gray-90">
                    {{ currentAccountId || '未登录' }}
                </code>
            </div>

            <button
                type="button"
                class="th-btn th-btn-primary mt-5"
                :disabled="!canResetOnboarding || rewardResetStatus === 'resetting'"
                :aria-busy="rewardResetStatus === 'resetting'"
                @click="resetFirstTaskReward"
            >
                {{ rewardResetStatus === 'resetting' ? '重置中…' : '重置赠送积分并刷新' }}
            </button>

            <p
                v-if="rewardResetStatus === 'failed'"
                class="mt-3 text-sm text-red-500"
                role="alert"
            >
                重置失败，请确认当前连接的是 local、QA 或 Stage 环境。
            </p>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { HeaderServiceProxy } from '@telecom/shared/services/header-service/header-service-proxy';
import {
    OnboardingStateService,
    type OnboardingStateService as OnboardingStateServiceType,
} from '@/features/onboarding/common';
import {
    FirstTaskRewardApi,
    type FirstTaskRewardApi as FirstTaskRewardApiType,
} from '@/features/onboarding/first-task-reward/first-task-reward-api';
import {
    TRACK_EVENT_DEBUG_EXPANDED_STORAGE_KEY,
    TRACK_EVENT_DEBUG_STORAGE_KEY,
    useTrackEventDebug,
} from '@/presentation/composables/use-track-event-debug';
import {
    DEBUG_REGION_STORAGE_KEY,
    INVALID_SEARCH_FORCE_ENABLE_STORAGE_KEY,
    type DebugRegion,
    useDebugSettings,
    VERSION_SWITCH_DEBUG_STORAGE_KEY,
} from './use-debug-settings';
import {
    clearAllCookies,
    createVisitorId,
    getVisitorIdCookie,
    setVisitorIdCookie,
} from './utils/visitor-cookie';

defineOptions({
    name: 'DebugContent',
});

const LOCAL_STORAGE_WHITELIST = new Set([
    VERSION_SWITCH_DEBUG_STORAGE_KEY,
    INVALID_SEARCH_FORCE_ENABLE_STORAGE_KEY,
    DEBUG_REGION_STORAGE_KEY,
    TRACK_EVENT_DEBUG_STORAGE_KEY,
    TRACK_EVENT_DEBUG_EXPANDED_STORAGE_KEY,
]);
const visitorId = ref(getVisitorIdCookie() || '');
const {
    forceEnableNewVersion,
    forceEnableInvalidSearch,
    debugRegionOverride,
} = useDebugSettings();
const { enabled: trackEventDebugEnabled } = useTrackEventDebug();
const headerServiceProxy = injectService<HeaderServiceProxy>(HeaderServiceProxy);
const onboardingStateService = injectService<OnboardingStateServiceType>(OnboardingStateService);
const firstTaskRewardApi = injectService<FirstTaskRewardApiType>(FirstTaskRewardApi);
const onboardingResetStatus = ref<'idle' | 'resetting' | 'failed'>('idle');
const rewardResetStatus = ref<'idle' | 'resetting' | 'failed'>('idle');
const currentAccountId = computed(() => String(
    headerServiceProxy.headerService.account?._info.account_id ?? '',
));
const canResetOnboarding = computed(() => Boolean(
    headerServiceProxy.headerService.account?.isLogin && currentAccountId.value,
));

function clearLocalStorageExceptWhitelist() {
    Object.keys(localStorage).forEach(key => {
        if (!LOCAL_STORAGE_WHITELIST.has(key)) {
            localStorage.removeItem(key);
        }
    });
}

function switchVisitor() {
    updateVisitor(createVisitorId());
}

function updateVisitor(nextVisitorId: string) {
    clearLocalStorageExceptWhitelist();
    sessionStorage.clear();
    clearAllCookies();

    setVisitorIdCookie(nextVisitorId);

    if (getVisitorIdCookie() !== nextVisitorId) {
        console.error('[debug] failed to update visitor_id cookie');
        return;
    }

    window.location.reload();
}

function toggleForceEnableNewVersion() {
    forceEnableNewVersion.value = !forceEnableNewVersion.value;
}

function toggleForceEnableInvalidSearch() {
    forceEnableInvalidSearch.value = !forceEnableInvalidSearch.value;
}

function toggleTrackEventDebug() {
    trackEventDebugEnabled.value = !trackEventDebugEnabled.value;
}

function handleDebugRegionChange(region: DebugRegion | null) {
    debugRegionOverride.value = region;
    window.location.reload();
}

async function resetOnboardingState() {
    if (!canResetOnboarding.value || onboardingResetStatus.value === 'resetting') return;

    onboardingResetStatus.value = 'resetting';
    try {
        await onboardingStateService.resetState(currentAccountId.value);
        window.location.reload();
    }
    catch (error) {
        onboardingResetStatus.value = 'failed';
        console.error('[debug] failed to reset onboarding state', error);
    }
}

async function resetFirstTaskReward() {
    if (!canResetOnboarding.value || rewardResetStatus.value === 'resetting') return;

    rewardResetStatus.value = 'resetting';
    try {
        await firstTaskRewardApi.reset();
        await onboardingStateService.updateState(
            currentAccountId.value,
            {
                first_task_reward_prompt_seen: false,
            },
            'required',
        );
        window.location.reload();
    }
    catch (error) {
        rewardResetStatus.value = 'failed';
        console.error('[debug] failed to reset first task reward', error);
    }
}
</script>
